import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import languageReducer from 'store/language.slice';
import storageSession from 'redux-persist/lib/storage/session';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import authReducer from 'store/auth.slice';
import appReducer from 'store/app.slice';
import notificationReducer from 'store/notification.slice';
import customerReducer from './customer.slice';
import internalUserReducer from 'store/internal.user.slice';
import assetReducer from 'store/asset.slice';
import externalUserReducer from 'store/external.user.slice';
import persistedReducer from 'store/persisted.slice';
import operatorReducer from 'store/operator.slice';
import ruleReducer from 'store/rule.slice';
import hierarchyReducer from 'store/hierarchy.slice';
import deviceReducer from 'store/device.slice';
import tabletReducer from 'store/tablet.slice';
import tagReducer from 'store/tag.slice';
import fuelAnalyticsReducer from 'store/fuelAnalytics.slice';
import squaresMaintenanceReducer from 'store/SquaresStore/MaintenanceSquaresState.slice';
import squaresDashboardReducer from 'store/SquaresStore/DashboardSquaresState.slice';
import squaresWorkOrdersReducer from 'store/SquaresStore/WorkOrdersSquaresState.slice';
import squaresPartsRecommendationsReducer from 'store/SquaresStore/PartsRecommendationsState.slice';
import squaresPartsMaintenanceExpenditureReducer from 'store/SquaresStore/PartsMaintenanceExpenditureState.slice';
import fuelAnalyticsToolTipReducer from './fuelAnalyticsToolTip.slice';
import fuelAnalyticsAssetListReducer from './fuelAnalyticsAssetList.slice';
import nonHaulTruckfuelAnalyticsReducer from './nonHaulTrucks.slice';
import ruleNotificationsReducer from 'store/ruleNotification.slice';
import engineHoursReducer from './engine.hours.slice';
import exportDataReducer from './exportData.slice';
import dtcReducer from './dtc.slice';
import idleAnalyticsReducer from 'store/idleAnalytics.slice';
import weatherReducer from 'store/weather.slice';
import fuelPriceReducer from 'store/fuelPrice.slice';
import survivalAnaysisOverview from 'store/survivalAnaysisStore/overview.slice';
import productionReducer from 'store/production.slice';
import utilizationReducer from './utilization.slice';
import targetReducer from './targets.slice';
import tireProfileReducer from './tireProfile.slice';
import tagThresholdReducer from './tag.threshold.slice';
import driverBehaviorReducer from './driver.behavior.slice';
import geoReducer from './geo.slice';
import inclinometerReducer from './inclinometer.slice';
import assetHealthReducer from './asset.health.slice';
import sensorAnalysisReducer from './sensorAnalysis.slice';
import tireSensorReducer from './tire.sensor.slice';
import sqauresWorkOrderCreationFormReducer from './SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import sqauresWorkOrderManagementReducer from './SquaresStore/WorkOrderManagement/WorkOrderManagementState.slice';
import mlInsightsReducer from './ml-insights.slice';
import fleetPlanningReducer from './fleet-planning.slice';
import tirePatternReducer from './tire-pattern.slice';
import makeModelDetailsReducer from './make-model-details.slice';
import tireInventoryReducer from './tire-inventory.slice';
import tireInstallationReducer from './tire-installation.slice';
import tireLeakageReducer from 'handlers/tire-leakage.handler';
import reliabilityReducer from './survivalAnaysisStore/reliability.slice';
import navigationConfigReducer from './navigation-config.slice';
import survivabilityReducer from './survivalAnaysisStore/survivability.slice';
import planningAidReducer from './planningAid.slice';
import squaresAlertReducer from './SquaresStore/AlertsSquares.slice';
import customerShiftConfigReducer from './customer-shift-config.slice';
import siteConfigReducer from './site-config.slice';
import pushNotificationPreferenceReducer from './pushNotificationPreference.slice';
import operatorSessionSwitchReducer from './operator-session-switch.slice';

// constant for store reset
export const RESET_STORE = 'RESET_STORE';

export const combinedReducers = combineReducers({
  // add reducers
  authReducer,
  appReducer,
  notificationReducer,
  languageReducer,
  customerReducer,
  internalUserReducer,
  externalUserReducer,
  persistedReducer,
  assetReducer,
  operatorReducer,
  ruleReducer,
  hierarchyReducer,
  deviceReducer,
  tabletReducer,
  tagReducer,
  fuelAnalyticsReducer,
  squaresMaintenanceReducer,
  squaresDashboardReducer,
  squaresWorkOrdersReducer,
  squaresPartsRecommendationsReducer,
  squaresPartsMaintenanceExpenditureReducer,
  fuelAnalyticsToolTipReducer,
  fuelAnalyticsAssetListReducer,
  nonHaulTruckfuelAnalyticsReducer,
  ruleNotificationsReducer,
  engineHoursReducer,
  exportDataReducer,
  dtcReducer,
  idleAnalyticsReducer,
  weatherReducer,
  fuelPriceReducer,
  survivalAnaysisOverview,
  productionReducer,
  utilizationReducer,
  targetReducer,
  tireProfileReducer,
  tagThresholdReducer,
  driverBehaviorReducer,
  geoReducer,
  inclinometerReducer,
  assetHealthReducer,
  sensorAnalysisReducer,
  tireSensorReducer,
  sqauresWorkOrderCreationFormReducer,
  sqauresWorkOrderManagementReducer,
  mlInsightsReducer,
  fleetPlanningReducer,
  tirePatternReducer,
  makeModelDetailsReducer,
  tireInventoryReducer,
  tireInstallationReducer,
  tireLeakageReducer,
  reliabilityReducer,
  customerShiftConfigReducer,
  navigationConfigReducer,
  survivabilityReducer,
  planningAidReducer,
  squaresAlertReducer,
  siteConfigReducer,
  pushNotificationPreferenceReducer,
  operatorSessionSwitchReducer,
});

// wrapper reducer to intercept RESET_STORE
export const rootReducer = (
  state: ReturnType<typeof combinedReducers> | undefined,
  action: any
) => {
  if (action.type === RESET_STORE) {
    // Flush the entire store
    state = undefined;
  }
  return combinedReducers(state, action);
};

const persistConfig = {
  key: 'root',
  version: 3,
  storage: storageSession,
  // only persisted reducer will be persisted
  whitelist: ['persistedReducer', 'siteConfigReducer'],
};

const persistedReducers = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
